import React from 'react';
import useIsMobile from '../../hooks/useIsMobile';

// RankingTable condivisa — self-responsive, puramente strutturale (zero dominio).
// Desktop: tabella 4 colonne (POS · nome · valore · coda). Mobile (<768px): resa
// "fit" a 3 celle senza scroll — POS · nome (valore sotto) · coda.
// Guscio, POS, colonna nome e sub-riga valore sono identici per ogni lista; ciò
// che varia è iniettato dai call-site:
//   - getSub(row)   OBBLIGATORIA — formattazione del valore € (nessun default:
//                   un default fallirebbe in silenzio per una lista futura).
//   - renderCoda(row, { isMobile })  cella di coda: ritorna un <td> su desktop,
//                   un <div> su mobile (così ogni lista controlla la propria coda
//                   — %/barra, conteggio, StatoBadge — con resa mobile≠desktop).
//   - getNome(row)  default row.nome (qui l'errore si vede subito, quindi ok).
// StatoBadge e fmtEur NON sono importati qui: vengono iniettati via getSub/renderCoda.
const CARD_SHADOW = '0 6px 18px rgba(35,38,47,0.08)';
const ROW_BG = (i) => (i % 2 === 0 ? 'var(--ds-bg)' : 'var(--chip)');

// Coda "% sul totale" (Famiglia A: Fornitori/Clienti/Cantieri) — barra fit a
// width:88 su mobile, width:80 in <td> su desktop. Export riusabile per non
// duplicare il markup della barra fra le tre liste che la condividono.
// CONVENZIONE: assume `row.pct` — stringa numerica della percentuale sul totale
// (es. "42.3" / "42.35"). Chi usa questo helper deve fornirla nelle righe.
export function renderBarraCoda(row, { isMobile }) {
  const fill = `${Math.min(100, parseFloat(row.pct))}%`;
  if (isMobile) {
    return (
      <div style={{ width: 88 }}>
        <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--ink)', textAlign: 'right' }}>{row.pct}%</div>
        <div style={{ height: 6, borderRadius: 3, background: 'var(--chip)', width: 88, marginTop: 3 }}>
          <div style={{ width: fill, height: 6, borderRadius: 3, background: 'var(--brand)' }} />
        </div>
      </div>
    );
  }
  return (
    <td style={{ padding: '11px 14px', minWidth: 100 }}>
      <span style={{ fontSize: 11, color: 'var(--ds-muted)' }}>{row.pct}%</span>
      <div style={{ height: 6, borderRadius: 3, background: 'var(--chip)', width: 80, marginTop: 3 }}>
        <div style={{ width: fill, height: 6, borderRadius: 3, background: 'var(--brand)' }} />
      </div>
    </td>
  );
}

export default function RankingTable({
  title, colNome, colValore, colCoda,
  rows, getNome = (row) => row.nome, getSub, renderCoda,
}) {
  if (typeof getSub !== 'function') {
    throw new Error('RankingTable: la prop getSub è obbligatoria (formattazione valore esplicita, nessun default).');
  }
  const isMobile = useIsMobile();

  let body;
  if (rows.length === 0) {
    body = <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--ds-muted)', fontSize: 11 }}>Nessun dato disponibile</div>;
  } else if (isMobile) {
    // Resa "fit" mobile: 3 colonne, coda sempre visibile senza swipe.
    body = (
      <div>
        {rows.map((row, i) => (
          <div key={row.pos} style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 12, alignItems: 'center', padding: '11px 16px', borderTop: '1px solid var(--ds-border)', background: ROW_BG(i) }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ds-muted)', minWidth: 14, textAlign: 'center' }}>{row.pos}</span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getNome(row)}</div>
              <div style={{ fontSize: 11, color: 'var(--ds-muted)', marginTop: 1 }}>{getSub(row)}</div>
            </div>
            {renderCoda(row, { isMobile: true })}
          </div>
        ))}
      </div>
    );
  } else {
    body = (
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr style={{ background: 'var(--chip)', borderBottom: '2px solid var(--ds-border)' }}>
          {['POS', colNome, colValore, colCoda].map(h => (
            <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
          ))}
        </tr></thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.pos} style={{ borderTop: '1px solid var(--ds-border)', background: ROW_BG(i) }}>
              <td style={{ padding: '11px 14px', fontSize: 11, color: 'var(--ds-muted)', fontWeight: 500 }}>{row.pos}</td>
              <td style={{ padding: '11px 14px', fontSize: 11, color: 'var(--ink)', fontWeight: 500 }}>
                <div style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getNome(row)}</div>
              </td>
              <td style={{ padding: '11px 14px', fontSize: 11, fontWeight: 500, color: 'var(--ink)', whiteSpace: 'nowrap' }}>
                {getSub(row)}
              </td>
              {renderCoda(row, { isMobile: false })}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  return (
    <div style={{ background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', border: '1px solid var(--ds-border)', boxShadow: CARD_SHADOW, overflow: 'hidden' }}>
      <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--ds-border)' }}>
        <span style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 14, color: 'var(--ink)' }}>{title}</span>
      </div>
      {body}
    </div>
  );
}
